import React, { useState, useEffect } from "react";
import { Table, Button, Modal } from "react-bootstrap";
import Layout from "../../components/Layout/Layout"; // Composant Layout qui contient la structure générale de la page
import HeaderWithFilter from "../../components/Layout/HeaderWithFilter"; // Composant pour l'en-tête avec filtre
import Loader from "../../components/Layout/Loader"; // Composant pour le loader
import ConfirmPopup from "../../components/Layout/ConfirmPopup"; // Composant de modal de confirmation pour la suppression d'commande
import SearchBar from "../../components/Layout/SearchBar"; // Composant pour la barre de recherche
import { fetchWithToken } from "../../utils/fetchWithToken"; // Importation d'une fonction utilitaire pour les requêtes avec token

const statusColors = {
  "EN ATTENTE": "bg-warning",
  CONFIRMÉE: "bg-primary",
  LIVRÉE: "bg-success",
  ANNULÉE: "bg-danger",
};

const CommandeList = () => {
  const [commandes, setCommandes] = useState([]);
  const [sortedCommandes, setSortedCommandes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedCommande, setSelectedCommande] = useState(null);
  const [filter, setFilter] = useState(""); // État pour le filtre
  const [sortOption, setSortOption] = useState(""); // État pour l'option de tri

  const [showDetails, setShowDetails] = useState(false);

  /** ================= FETCH ================= */
  useEffect(() => {
    const fetchCommandes = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await fetchWithToken(
          `${process.env.REACT_APP_API_BASE_URL}/commandes`
        );

        if (!response.ok) {
          throw new Error("Erreur lors du chargement des commandes");
        }

        const data = await response.json();
        setCommandes(data.data || []);
        setSortedCommandes(data.data || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCommandes();
  }, []);
  /* */

  const handleShowDetails = (commande) => {
    setSelectedCommande(commande);
    setShowDetails(true);
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedCommande(null);
  };

  /** ================= DELETE ================= */
  const handleDelete = async () => {
    if (!selectedCommande) return;

    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_API_BASE_URL}/commandes/${selectedCommande.id}`,
        { method: "DELETE" }
      );

      const result = await response.json();

      if (result.status === "deleted") {
        alert("Commande supprimée avec succès !");
        setCommandes((prev) =>
          prev.filter((c) => c.id !== selectedCommande.id)
        );
        setSortedCommandes((prev) =>
          prev.filter((c) => c.id !== selectedCommande.id)
        );
      } else {
        alert("Suppression échouée");
      }
    } catch {
      setError("Erreur lors de la suppression");
    } finally {
      setShowModal(false);
      setSelectedCommande(null);
    }
  };

  /** ================= FILTER ================= */
  const filteredCommandes = sortedCommandes.filter((c) =>
    [c.nom, c.email, c.numero, c.commune]
      .join(" ")
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  const formatNomAbrege = (nom = "") => {
    const parts = nom.trim().split(/\s+/);

    if (parts.length === 1) {
      return parts[0];
    }

    return `${parts[0]} ${parts[1][0].toUpperCase()}.`;
  };

  return (
    <Layout>
      <div className="container mt-3">
        {error && <div className="alert alert-danger">{error}</div>}

        {loading ? (
          <div
            className="d-flex justify-content-center"
            style={{ height: "60vh" }}
          >
            <Loader />
          </div>
        ) : (
          <>
            <SearchBar
              placeholder="Rechercher une commande..."
              onSearch={setSearchQuery}
              delay={300}
            />

            <HeaderWithFilter
              allowedRoles={["dev", "super_admin"]}
              title="Commandes"
              main={commandes.length || null}
              filter={filter}
              setFilter={setFilter}
              filterOptions={[
                { value: "", label: "Tous" },
                { value: "EN ATTENTE", label: "En attente" },
                { value: "CONFIRMÉE", label: "Confirmée" },
                { value: "LIVRÉE", label: "Livré" },
                { value: "ANNULÉE", label: "Annulée" },
              ]}
              sortOption={sortOption}
              setSortOption={setSortOption}
              dataList={commandes}
              setSortedList={setSortedCommandes}
              alphaField="nom"
              dateField="created_at"
            />

            <Table hover responsive className="centered-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Client</th>
                  <th>Téléphone</th>
                  {/* <th>Commune</th> */}
                  <th>Articles</th>
                  <th>Total</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredCommandes.length ? (
                  filteredCommandes
                    .filter((commande) => !filter || commande.status === filter)
                    .map((commande, index) => (
                      <tr key={commande.id}>
                        <td>{index + 1}</td>
                        <td>{formatNomAbrege(commande.nom)}</td>
                        <td>{commande.numero}</td>
                        {/* <td>{commande.commune}</td> */}
                        <td>{commande.total_items}</td>
                        <td>
                          <strong>
                            {Number(commande.total_price).toLocaleString()} FCFA
                          </strong>
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              statusColors[commande.status] || "bg-secondary"
                            }`}
                          >
                            {commande.status}
                          </span>
                        </td>
                        <td className="table-operations">
                          <div className="d-flex align-items-stretch justify-content-center gap-2 h-100">
                            <button
                              onClick={() => handleShowDetails(commande)}
                              className="btn btn-sm btn-info"
                            >
                              <i className="fa fa-eye"></i>
                            </button>
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => {
                                setSelectedCommande(commande);
                                setShowModal(true);
                              }}
                            >
                              <i className="fa fa-trash"></i>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center">
                      Aucune commande trouvée
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </>
        )}
      </div>

      <ConfirmPopup
        show={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleDelete}
        title="Supprimer la commande"
        body={
          <p>
            Supprimer la commande de <strong>{selectedCommande?.nom}</strong> ?
          </p>
        }
      />

      {/* Modal détails commande */}
      {selectedCommande && (
        <Modal
          show={showDetails}
          onHide={handleCloseDetails}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Détails de la commande</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {selectedCommande && (
              <div className="p-3 p-md-4">
                {/* ===== EN-TÊTE ===== */}
                <div className="text-center mb-4">
                  <h2 className="text-primary mb-1">{selectedCommande.nom}</h2>
                  <span
                    className={`fs-6 badge ${
                      statusColors[selectedCommande.status] || "bg-secondary"
                    }`}
                  >
                    {selectedCommande.status}
                  </span>
                </div>

                {/* ===== INFORMATIONS CLIENT ===== */}
                <div className="bg-body rounded shadow-sm p-3 mb-3">
                  <h5 className="mb-3">👤 Informations client</h5>
                  <div className="row">
                    {selectedCommande.email && (
                      <div className="col-lg-6 mb-2">
                        <strong>Email :</strong> {selectedCommande.email}
                      </div>
                    )}
                    <div className="col-lg-6 mb-2">
                      <strong>Téléphone :</strong> {selectedCommande.numero}
                    </div>
                    <div className="col-lg-6 mb-2">
                      <strong>Commune :</strong> {selectedCommande.commune}
                    </div>
                  </div>
                </div>

                {/* ===== RÉCAP COMMANDE ===== */}
                <div className="bg-body rounded shadow-sm p-3 mb-3">
                  <h5 className="mb-3">🧾 Récapitulatif</h5>
                  <div className="row">
                    <div className="col-lg-6 mb-2">
                      <strong>Articles :</strong> {selectedCommande.total_items}
                    </div>
                    <div className="col-lg-6 mb-2">
                      <strong>Total :</strong>{" "}
                      <span className="text-success fw-bold">
                        {Number(selectedCommande.total_price).toLocaleString()}{" "}
                        FCFA
                      </span>
                    </div>
                  </div>
                </div>

                {/* ===== PANIER ===== */}
                {Array.isArray(selectedCommande.cart) &&
                  selectedCommande.cart.length > 0 && (
                    <div className="bg-body rounded shadow-sm p-3 mb-3">
                      <h5 className="mb-3">🛒 Produits commandés</h5>

                      <div className="table-responsive">
                        <table className="table table-sm align-middle table-bordered">
                          <thead>
                            <tr>
                              <th>Produit</th>
                              <th>Catégorie</th>
                              <th>Prix</th>
                              <th>Qté</th>
                              <th>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedCommande.cart.map((item, index) => (
                              <tr key={index}>
                                <td className="d-flex align-items-center gap-2">
                                  <img
                                    src={item.image}
                                    alt={item.name}
                                    width="40"
                                    height="40"
                                    className="rounded"
                                  />
                                  <span className="text-uppercase">
                                    {item.name}
                                  </span>
                                </td>
                                <td>{item.category}</td>
                                <td>
                                  {Number(item.price).toLocaleString()} FCFA
                                </td>
                                <td className="text-center">{item.quantity}</td>
                                <td className="fw-bold">
                                  {(
                                    item.price * item.quantity
                                  ).toLocaleString()}{" "}
                                  FCFA
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
              </div>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseDetails}>
              Fermer
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Layout>
  );
};

export default CommandeList;
